import { pageTitle } from '@palaccount/config';
import type { ReceiptResponse } from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { CheckCircle2, Eraser, Info, Loader2, PiggyBank, Send, Trash2, Undo2, Wand2, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount, Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentToolbar } from '@/components/document-toolbar';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useCashAccountOptions, useCreditAccountOptions } from '@/features/accounts/api';
import { FxPanel, useRateWarningGate } from '@/features/documents/fx-panel';
import { useCurrencyPickerCodes, useCurrencyTable } from '@/features/fx/api';
import { DocCurrencyCell, useDocCurrency } from '@/features/documents/doc-currency';
import { PARTNERS_KEY } from '@/features/partners/api';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { INVOICES_KEY } from '@/features/sales/api';
import { CreditNoteSources } from '@/features/sales/credit-note-sources';
import { usePagedState, usePageRange } from '@/lib/paging';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import { autoAllocateAmount, autoAllocateOldestFirst, computeAllocationView } from './allocation';
import {
  blankTender,
  tendersFromResponse,
  tendersPayload,
  tendersVoucherTotal,
  validateTenders,
  type TenderRow,
} from './tenders';
import { TendersGrid } from './tenders-grid';
import {
  RECEIPTS_KEY,
  useAutoAllocate,
  useAutoAllocatePreview,
  useCancelReceipt,
  useCreateReceipt,
  useDeleteReceipt,
  useOpenInvoices,
  useReceipt,
  useReverseReceipt,
  useSetAllocations,
  useUpdateReceipt,
} from './api';

type ReceiptType = ReceiptResponse['receiptType'];

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;

interface GridRow {
  invoiceId: string;
  invoiceCode: string;
  invoiceDate: string;
  invoiceTotal: string;
  alreadyPaid: string;
  balanceDue: string;
}

/**
 * FIN-03 — the Receipt Voucher: top panel, the six-column allocation grid
 * with its four-state strip, oldest-first auto-allocate, the overpayment →
 * customer-credit banner, and Post behind `accounting.approve`. One screen
 * serves /new (allocations travel with the create) and /:id (a draft stays
 * editable; posted/cancelled is locked in every field — BR-FIN-03-13).
 *
 * Both receipt types: a CUSTOMER payment (customer + allocation grid) and
 * OTHER INCOME (category, description, and a credit-account picker —
 * BR-FIN-03-05). Both account pickers read the accounts route: "Received
 * into" offers the postable cash-family accounts only (BR-FIN-03-04).
 */
export function ReceiptEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { receiptId } = useParams<{ receiptId: string }>();
  const isNew = !receiptId;
  const [searchParams] = useSearchParams();

  const { data: receipt, isPending: loadingReceipt } = useReceipt(receiptId);
  const { options: cashOptions } = useCashAccountOptions();
  const { options: creditOptions } = useCreditAccountOptions();
  const createReceipt = useCreateReceipt();
  const updateReceipt = useUpdateReceipt(receiptId ?? '');
  const setAllocationsMutation = useSetAllocations(receiptId ?? '');
  const autoAllocateMutation = useAutoAllocate(receiptId ?? '');
  /** The unsaved voucher's walk — the server's, over the whole open set. */
  const autoAllocatePreviewMutation = useAutoAllocatePreview();
  const deleteReceipt = useDeleteReceipt(receiptId ?? '');
  const cancelReceipt = useCancelReceipt(receiptId ?? '');
  const reverseReceipt = useReverseReceipt(receiptId ?? '');

  const canEdit = useCan(isNew ? 'accounting.create' : 'accounting.edit');
  const canPost = useCan('accounting.approve');

  const [receiptDate, setReceiptDate] = useDocumentDate();
  const [receiptType, setReceiptType] = useState<ReceiptType>('customer');
  // Arriving from a sales invoice's "Record payment" (?partnerId=&invoiceId=)
  // pre-selects the customer and pre-allocates that invoice — the customer-side
  // mirror of the payment voucher's ?partnerId=&billId= entry.
  const [partnerId, setPartnerId] = useState(isNew ? (searchParams.get('partnerId') ?? '') : '');
  /*
   * GEN-50 — FIN-03 §5's picker: "a search box matching name or code, then
   * the customers, each with its outstanding balance". The balance rides on
   * the row as its hint; the empty result is the spec's own sentence.
   */
  const customerPicker = usePartnerPicker(partnerId || null, { type: 'customer' }, (partner) => ({
    id: partner.id,
    label: partner.name,
    hint: `${partner.code} · ${partner.owedToUs}`,
    inactive: !partner.isActive,
  }));

  const [incomeCategory, setIncomeCategory] = useState('');
  const [incomeDescription, setIncomeDescription] = useState('');
  const [creditAccountId, setCreditAccountId] = useState('');
  /**
   * S6 — THE TENDERS (RV-02…RV-06): one row per way the money came in. The
   * default is one cash row in the company's base currency, due today. The
   * voucher's "amount" is DERIVED from these rows below, never typed apart.
   */
  const base = useBaseCurrency();
  const [tenders, setTenders] = useState<TenderRow[]>(() => [blankTender(base, receiptDate)]);
  const [notes, setNotes] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** BR-FIN-03-17's ask-why dialog for the posted-voucher reversal. */
  const [reverseAsk, setReverseAsk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** BR-MD-11-14's question, in ADM-02 §8's shared box (BR-PERM-15). */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  /**
   * `window.confirm` is neither of §16.5.3's two patterns, so BR-PERM-15
   * (spec line 35168) forbade it; the chrome went first (recorded then as
   * "chrome removal only"). GEN-59's OTHER HALF IS NOW PAID TOO (spec line
   * 3806: the cancel asks for a reason AND STORES IT ON THE RECORD): the
   * contract carries the reason (receipts.ts cancel body: required, 1..500),
   * the service writes `cancel_reason` on the voucher and the reason onto
   * the audit row (20260903100000), and the cancelled banner below reads it
   * back. So this is a `ReasonBox` exactly like `reverseAsk` below — the
   * shape the two-button box's own comment said it should become.
   */
  const [cancelAsk, setCancelAsk] = useState(false);
  const loadedId = useRef<string | null>(null);

  const status = receipt?.status ?? 'draft';
  const locked = status !== 'draft';
  const editable = !locked && canEdit;

  useEffect(() => {
    document.title = pageTitle(t('receipts.editorTitle'));
  }, [t]);

  // Adopt the stored voucher wholesale (the server's figures are the truth).
  useEffect(() => {
    if (receipt && loadedId.current !== `${receipt.id}:${receipt.createdAt}`) {
      loadedId.current = `${receipt.id}:${receipt.createdAt}`;
      setReceiptDate(receipt.receiptDate);
      setReceiptType(receipt.receiptType);
      setPartnerId(receipt.partnerId ?? '');
      setIncomeCategory(receipt.incomeCategory ?? '');
      setIncomeDescription(receipt.incomeDescription ?? '');
      setCreditAccountId(receipt.creditAccountId ?? '');
      // A legacy draft typed straight into the head has no rows yet; it is
      // offered one in its own currency so the amount can be re-stated.
      setTenders(
        (receipt.tenders ?? []).length > 0
          ? tendersFromResponse(receipt.tenders)
          : receipt.status === 'draft'
            ? [blankTender(receipt.currency ?? base, receipt.receiptDate, { accountId: receipt.receivedIntoAccountId })]
            : [],
      );
      setNotes(receipt.notes ?? '');
      setAlloc(Object.fromEntries(receipt.allocations.map((row) => [row.invoiceId, row.amount])));
    }
  }, [receipt]);

  const partner = customerPicker.byId.get(partnerId);
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  // Per-document currency (2026-10-30): the voucher's own dropdown in front of
  // the chain. A pick clears the allocation — the rows belonged to the other
  // currency's invoices (the server clears them the same way).
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: receipt?.currency,
    partnerCurrency: partner?.currency ?? base,
    initialPick: isNew ? searchParams.get('currency') : null,
    onPick: (code) => {
      // A tender row not yet filled in follows the voucher into the new currency.
      setTenders((rows) => rows.map((row) => (row.amount.trim() === '' ? { ...row, currency: code } : row)));
      setAlloc({});
      setBalances(new Map());
      invoicePaging.resetPage();
    },
  });
  const currency = docCurrency.currency;
  /** Sent only for a customer receipt that picked one; other income keeps the server's rule. */
  const currencyBody = () => (receiptType === 'customer' ? docCurrency.body() : {});
  /**
   * The currency the open list and the preview are asked in: the pick, else
   * the saved voucher's; absent = the server's default (the partner's), so a
   * new voucher never asks in the base for the moment before the partner loads.
   */
  const settleCurrency = docCurrency.picked || receipt?.currency || undefined;
  /**
   * The rate table, for the grid's Rate defaults and for the voucher-currency
   * total when a tender is in another currency (mirrors the server's
   * `summarise`; the server's figure is the truth once saved).
   */
  const { data: currencyTable } = useCurrencyTable();
  const currencyCodes = useCurrencyPickerCodes();
  const rateFor = (code: string): string | null =>
    currencyTable?.rows.find((row) => row.code === code)?.rate ?? null;
  const voucherRate = currency === base ? '1' : rateFor(currency);
  /**
   * The amount received, as the strip and the posting gate read it: the
   * tenders' total in the voucher's currency; '' while no row has a figure
   * yet (BR-FIN-03-10's blank-amount shortcut keeps its meaning).
   */
  const amount = useMemo(() => {
    const anyTyped = tenders.some((row) => MONEY_RE.test(row.amount.trim()));
    return anyTyped ? tendersVoucherTotal(tenders, currency, base, voucherRate) : '';
  }, [tenders, currency, base, voucherRate]);
  /**
   * Auto-allocate's write-back (BR-FIN-03-10) lands in the ONE row — a
   * one-tender voucher is the old single-amount voucher. Several rows are the
   * amount; the shortcut has nothing to write back to and leaves them.
   */
  const setAmount = (next: string | ((current: string) => string)) => {
    const value = typeof next === 'function' ? next(amount) : next;
    setTenders((rows) => {
      if (rows.length > 1) return rows;
      const row = rows[0] ?? blankTender(currency, receiptDate);
      return [{ ...row, currency: row.amount.trim() === '' ? currency : row.currency, amount: value }];
    });
  };

  /**
   * The grid's own page (§3 step 12) — `?invoicePage=`, named because the
   * voucher screen is not a one-list screen and an unnamed `?page=` would be
   * claimed by whichever block asked first.
   */
  const invoicePaging = usePagedState(DEFAULT_PAGE_SIZE, 'invoice');

  /**
   * `invoicesLoading` is load-bearing, not decoration. Before the list has
   * arrived there is no allocation to conclude about, and the strip below
   * withholds its conclusion until there is (see `stripState`). It stays
   * false while a PAGE turns — `keepPreviousData` — so paging never flashes
   * the pending strip over a grid the user is reading.
   */
  const { data: invoicePage, isLoading: invoicesLoading } = useOpenInvoices(
    partnerId || null,
    invoicePaging.page,
    invoicePaging.pageSize,
    settleCurrency,
  );
  const openInvoices = invoicePage?.invoices;
  const invoiceRange = usePageRange(invoicePage, invoicePaging.pageSize);

  /**
   * BALANCE DUE FOR EVERY ROW THE GRID HAS SEEN — the whole-set balance
   * source `computeAllocationView` now takes (PAGINATION.md §11).
   *
   * It ACCUMULATES across pages rather than being rebuilt from the page,
   * which is the whole point: a row typed over-balance on page 1 has to stay
   * red — and keep Post refused — while the reader is on page 2. Cleared
   * beside `setAlloc({})` wherever the party changes, because a balance
   * belongs to the customer whose invoice it is.
   */
  const [balances, setBalances] = useState<ReadonlyMap<string, string>>(() => new Map());
  useEffect(() => {
    const seen: { invoiceId: string; balanceDue: string }[] = [
      ...(openInvoices ?? []),
      ...(receipt?.allocations ?? []),
    ];
    if (seen.length === 0) return;
    setBalances((current) => {
      let next: Map<string, string> | null = null;
      for (const row of seen) {
        if (current.get(row.invoiceId) === row.balanceDue) continue;
        next ??= new Map(current);
        next.set(row.invoiceId, row.balanceDue);
      }
      // Same map when nothing is new, so this cannot loop on itself.
      return next ?? current;
    });
  }, [openInvoices, receipt]);

  /**
   * The carried invoice from the sales invoice's "Record payment" link:
   * pre-allocate its full balance due once it appears in the customer's open
   * list. Once only — after that the reader owns the number.
   */
  const carriedInvoiceId = isNew ? searchParams.get('invoiceId') : null;
  const carriedInvoiceDone = useRef(false);
  useEffect(() => {
    if (!carriedInvoiceId || carriedInvoiceDone.current) return;
    const match = (openInvoices ?? []).find((invoice) => invoice.invoiceId === carriedInvoiceId);
    if (!match) return;
    carriedInvoiceDone.current = true;
    setAlloc((current) => ({ ...current, [carriedInvoiceId]: match.balanceDue }));
  }, [carriedInvoiceId, openInvoices]);

  /**
   * The grid: this PAGE of the customer's open invoices, oldest first, plus
   * any invoice this voucher already allocates that has since left the open
   * list.
   *
   * Those stragglers belong to the VOUCHER rather than to the open set, so
   * they have no page of their own; they are appended on the LAST page, which
   * is the one place that shows each of them exactly once and leaves them
   * reachable. Their amounts ride in the payload from every page — the grid
   * submits from `alloc`, not from here.
   */
  const rows: GridRow[] = useMemo(() => {
    const open: GridRow[] = (openInvoices ?? []).map((invoice) => ({ ...invoice }));
    if (invoicePaging.page < invoiceRange.pages) return open;
    for (const allocation of receipt?.allocations ?? []) {
      if (!open.some((row) => row.invoiceId === allocation.invoiceId)) {
        open.push({
          invoiceId: allocation.invoiceId,
          invoiceCode: allocation.invoiceCode,
          invoiceDate: allocation.invoiceDate,
          invoiceTotal: allocation.invoiceTotal,
          alreadyPaid: allocation.alreadyPaid,
          balanceDue: allocation.balanceDue,
        });
      }
    }
    return open;
  }, [openInvoices, receipt, invoicePaging.page, invoiceRange.pages]);

  /**
   * §5b's strip, over the WHOLE allocation — `alloc` is every page's cells,
   * `balances` is every page the grid has drawn. Sourcing this from `rows`
   * is PAGINATION.md §11's first finding: it made `totalApplied` and
   * BR-FIN-03-09's credit describe fifty invoices while saying "voucher".
   */
  const view = useMemo(
    () => computeAllocationView(amount, { amounts: alloc, balances }, currency),
    [amount, alloc, balances, currency],
  );

  // §12's preview names the account(s) the posting debits — the tenders'.
  const accountCodes = [
    ...new Set(
      tenders
        .map((row) => cashOptions.find((option) => option.id === row.accountId)?.code)
        .filter((code): code is string => code !== undefined),
    ),
  ];
  const accountCode =
    accountCodes.length > 0 ? accountCodes.join(' / ') : receipt?.receivedIntoAccountCode;
  const creditOption = creditOptions.find((option) => option.id === creditAccountId);

  /** BR-FIN-03-03 — choosing a customer clears the whole allocation. */
  /**
   * ERR-FIN-03-07 — changing the customer CLEARS the whole allocation, and
   * that is the correct behaviour rather than a lost edit: the rows being
   * allocated against belong to the old customer, so carrying the amounts
   * across would apply this money to invoices of somebody else.
   */
  const onPickCustomer = (id: string) => {
    setPartnerId(id);
    setAlloc({});
    // The balances belonged to the OLD customer's invoices, and the grid goes
    // back to its first page — a different party's open set is a different
    // list, which is BR-DSH-09-19's reset applied to the only filter here.
    setBalances(new Map());
    invoicePaging.resetPage();
  };

  /** The type toggle carries the panel with it; only a NEW voucher can flip. */
  const onPickType = (next: ReceiptType) => {
    if (!editable || !isNew || next === receiptType) return;
    setReceiptType(next);
    setPartnerId('');
    setAlloc({});
    setBalances(new Map());
    invoicePaging.resetPage();
  };

  const handleFailure = (
    thrown: unknown,
    permission: 'accounting.create' | 'accounting.edit' | 'accounting.delete' | 'accounting.approve',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw it away one expression later. `localizeServerCause` keeps it: a
    // refusal keeps the server's sentence localized (`RECEIPT_MESSAGES` are
    // mapped in STATIC_MESSAGE_KEYS), a request that never answered gets this
    // screen's own sentence rather than the browser's `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'receipts.errSaveFailed'));
  };

  /**
   * THE PAYLOAD, FROM `alloc` — EVERY PAGE — AND NEVER FROM `rows`.
   *
   * PAGINATION.md §3 step 13, and this is the line the whole rule was written
   * about. `setAllocations` REPLACES the collection server-side, so mapping
   * over the rows on screen behind a paged read sends one page and DELETES
   * the allocations on all the others: auto-allocate 90,000 across 120
   * invoices, stay on page 1, press Save, and seventy allocation rows are
   * gone and the voucher posts against invoices nobody chose — silently, from
   * a control that reported success.
   *
   * `alloc` is the editor's own state and is complete whether or not a row is
   * being drawn, which is what makes the paged grid safe. Its insertion order
   * is what the user (or the server's walk) produced, and the order does not
   * matter to a replace — but it is deterministic, so a test can pin it.
   *
   * `fin03-04-paged-allocation-save.test.tsx` is red against the `rows`
   * version of this expression and green against this one.
   */
  const allocationBody = () =>
    Object.entries(alloc)
      .map(([invoiceId, cell]) => ({ invoiceId, amount: cell.trim() }))
      .filter((row) => MONEY_RE.test(row.amount) && row.amount !== '0' && row.amount !== '0.00');

  const validate = (): string | null => {
    if (receiptType === 'customer' && !partnerId) return t('receipts.errCustomerRequired');
    if (receiptType === 'other_income' && !creditAccountId)
      return t('receipts.errCreditAccountRequired');
    const problem = validateTenders(tenders);
    if (problem === 'amountFormat') return t('receipts.errAmountFormat');
    if (problem === 'accountRequired') return t('receipts.errAccountRequired');
    if (problem === 'chequeNoRequired') return t('tenders.errChequeNoRequired');
    if (problem === 'dueDateRequired') return t('tenders.errDueDateRequired');
    if (problem === 'rateFormat') return t('tenders.errRateFormat');
    return null;
  };

  /**
   * Persists the voucher (create or update+allocations); returns it.
   * `navigateAfter=false` lets Post finish before the detail route mounts —
   * otherwise the fresh page's initial fetch (still a draft) races the post.
   */
  const save = async (navigateAfter = true): Promise<ReceiptResponse | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    try {
      if (isNew) {
        // The rows ARE the money; the head's method/amount/account are the
        // server's summary of them. A voucher with no finished row yet is
        // saved with its first row's account and no amount, as a draft.
        const rows = tendersPayload(tenders, 'receipt');
        const created = await createReceipt.mutateAsync({
          receiptDate,
          receiptType,
          ...currencyBody(),
          ...(rows.length > 0
            ? { tenders: rows }
            : {
                method: tenders[0]?.method === 'endorsed_check' ? 'cash' : (tenders[0]?.method ?? 'cash'),
                receivedIntoAccountId: tenders[0]?.accountId ?? '',
              }),
          ...(receiptType === 'customer'
            ? {
                partnerId,
                ...(allocationBody().length > 0 ? { allocations: allocationBody() } : {}),
              }
            : {
                creditAccountId,
                ...(incomeCategory.trim() ? { incomeCategory: incomeCategory.trim() } : {}),
                ...(incomeDescription.trim() ? { incomeDescription: incomeDescription.trim() } : {}),
              }),
          ...(notes.trim() ? { notes } : {}),
        });
        if (navigateAfter) void navigate(`/accounting/receipts/${created.id}`, { replace: true });
        return created;
      }
      const partnerChanged = receipt?.partnerId !== partnerId;
      const rows = tendersPayload(tenders, 'receipt');
      const updated = await updateReceipt.mutateAsync({
        receiptDate,
        ...(receiptType === 'customer' && partnerChanged ? { partnerId } : {}),
        ...currencyBody(),
        ...(rows.length > 0
          ? { tenders: rows }
          : tenders[0]?.accountId
            ? { receivedIntoAccountId: tenders[0].accountId }
            : {}),
        ...(receiptType === 'other_income'
          ? {
              creditAccountId,
              incomeCategory: incomeCategory.trim() === '' ? null : incomeCategory.trim(),
              incomeDescription: incomeDescription.trim() === '' ? null : incomeDescription.trim(),
            }
          : {}),
        notes: notes.trim() === '' ? null : notes,
      });
      if (receiptType === 'other_income') {
        loadedId.current = null;
        return updated;
      }
      const reallocated = await setAllocationsMutation.mutateAsync({
        allocations: allocationBody(),
      });
      loadedId.current = null;
      return reallocated;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'accounting.create' : 'accounting.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  /**
   * Auto-allocate, oldest first (BR-FIN-03-10). BOTH paths are the server's
   * walk now: the unsaved voucher asks the preview route, the saved draft asks
   * its own route and persists.
   *
   * The unsaved path used to fold the customer's whole open set in the browser,
   * which is why `GET /api/receipts/open-invoices` cannot be paged — a page of
   * a list something folds over prints a WRONG TOTAL rather than hiding a row.
   * The fold moved to the server so that the list can be paged later without
   * changing what the button computes.
   */
  const onAutoAllocate = async () => {
    setError(null);
    if (isNew) {
      // No customer picked: the open set is empty by construction, and asking
      // the server about a party nobody chose is a 404 rather than an answer.
      // The local mirror over an empty grid gives exactly the old result.
      if (!partnerId) {
        const empty = autoAllocateOldestFirst(amount, [], currency);
        setAlloc(empty.amounts);
        setAmount(empty.amountReceived);
        return;
      }
      setBusy(true);
      try {
        const amountToSpread = autoAllocateAmount(amount, currency);
        const preview = await autoAllocatePreviewMutation.mutateAsync({
          partnerId,
          ...(settleCurrency ? { currency: settleCurrency } : {}),
          ...(amountToSpread === undefined ? {} : { amount: amountToSpread }),
        });
        setAlloc(
          Object.fromEntries(preview.allocations.map((row) => [row.invoiceId, row.amount])),
        );
        // BR-FIN-03-10's write-back, computed over every open invoice rather
        // than over the ones the grid happens to be showing.
        setAmount(preview.amountReceived);
      } catch (thrown) {
        // Not `handleFailure`: that one denies a WRITE permission, and this
        // route carries the read permission anyone on this screen already
        // holds. A refusal here keeps the server's sentence; a request that
        // never answered gets the screen's own.
        setError(localizeServerCause(t, thrown, 'receipts.errSaveFailed'));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      // Persist the amount box first so the server allocates what is typed.
      const saved = await save();
      if (!saved) return;
      await autoAllocateMutation.mutateAsync();
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'accounting.edit');
    } finally {
      setBusy(false);
    }
  };

  /** BR-FIN-03-11 — Clear empties the cells, never the amount received. */
  const onClear = () => setAlloc({});

  const onPost = async () => {
    // BR-MD-11-14 at the confirming click, before the save that precedes it.
    if (!(await confirmRateWarning(receipt?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await save(false);
      if (!saved) return;
      // Post directly by the saved id — on /new the URL-bound hooks are stale.
      const response = await api.receipts.post({ params: { receiptId: saved.id }, body: {} });
      if (response.status !== 200) {
        if (response.status === 403) denyPermission('accounting.approve');
        const body = response.body;
        setError(
          localizeServerMessage(
            t,
            typeof body === 'object' && body !== null && 'message' in body
              ? String((body as { message: unknown }).message)
              : t('common.loadFailed'),
          ),
        );
        return;
      }
      loadedId.current = null;
      // Posting moved invoice balances and the customer's outstanding total.
      void queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
      if (isNew) void navigate(`/accounting/receipts/${saved.id}`, { replace: true });
    } catch (thrown) {
      handleFailure(thrown, 'accounting.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!receiptId) return;
    setDeleting(false);
    setBusy(true);
    try {
      await deleteReceipt.mutateAsync();
      void navigate('/accounting');
    } catch (thrown) {
      handleFailure(thrown, 'accounting.delete');
    } finally {
      setBusy(false);
    }
  };

  /** GEN-59: the ReasonBox hands over the typed reason; it rides the wire. */
  const onCancelDraft = async (reason: string) => {
    if (!receiptId) return;
    setCancelAsk(false);
    setBusy(true);
    try {
      await cancelReceipt.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'accounting.edit');
    } finally {
      setBusy(false);
    }
  };

  /**
   * BR-FIN-03-17 / R8 — the posted-voucher correction. The ReasonBox IS the
   * confirm: the reason is required (GEN-72's shape, audited server-side),
   * so Escape, click-outside or an empty box abandons the act (ADM-02 §8:
   * no browser chrome). The same pattern the bill's accept-variance uses.
   */
  const onReverse = async (reason: string) => {
    if (!receiptId) return;
    setReverseAsk(false);
    setBusy(true);
    setError(null);
    try {
      await reverseReceipt.mutateAsync(reason);
    } catch (thrown) {
      handleFailure(thrown, 'accounting.approve');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingReceipt) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  const amountAboveZero = MONEY_RE.test(amount.trim()) && !/^0+(\.0+)?$/.test(amount.trim());
  const postable =
    amountAboveZero &&
    validateTenders(tenders) === 'none' &&
    (receiptType === 'customer'
      ? Boolean(partnerId) && view.state !== 'over'
      : Boolean(creditAccountId));

  /**
   * The strip states a CONCLUSION about the allocation, and BR-FIN-03-09
   * governs what that conclusion says once there is one. Before the
   * open-invoice list lands there is no allocation to conclude about — the
   * rule's precondition is not met — yet computeAllocationView, seeing an
   * amount and no rows, reports the whole sum as customer credit. Announcing
   * "Excess held as customer credit: 90,000.00" while the system does not yet
   * know whether the customer has any open invoices is not BR-FIN-03-09 being
   * honoured; it is a confident answer to a question still in flight. A number
   * that is briefly wrong is worse than one that is honestly absent, so the
   * conclusion is WITHHELD until the grid is real.
   */
  const stripState = invoicesLoading ? 'pending' : view.state;
  const strip = {
    pending: {
      icon: <Loader2 className="size-4 animate-spin" aria-hidden />,
      className: 'border-t-muted-foreground/40 bg-muted/40 text-muted-foreground',
      text: t('receipts.stripPending'),
    },
    idle: {
      icon: <Info className="size-4" aria-hidden />,
      className: 'border-t-muted-foreground/40 bg-muted/40 text-muted-foreground',
      text: t('receipts.stripIdle'),
    },
    over: {
      icon: <XCircle className="size-4" aria-hidden />,
      className:
        'border-t-red-500 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
      text: t('receipts.stripOver'),
    },
    // ERR-FIN-03-06 — allocating LESS than the money received is allowed and
    // the amber strip is the whole of the answer: nothing to fix, the excess is
    // held as customer credit.
    credit: {
      icon: <PiggyBank className="size-4" aria-hidden />,
      className:
        'border-t-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
      text: t('receipts.stripCredit', { amount: view.unallocated }),
    },
    full: {
      icon: <CheckCircle2 className="size-4" aria-hidden />,
      className:
        'border-t-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
      text: t('receipts.stripFull'),
    },
  }[stripState];

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's question in ADM-02 §8's shared box. Rendered
          UNCONDITIONALLY, unlike the delete box below: this one is awaited, so
          a dialog that unmounted mid-question would leave the post hanging with
          no error anywhere. See `useRateWarningGate`. */}
      {rateWarningDialog}
      {/* ── Header: identity, chip, actions ────────────────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('receipts.editorTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {receipt ? <bdi dir="ltr">{receipt.code}</bdi> : t('receipts.codeNew')}
        </span>
        <DocStatusChip status={status} postedTone="green" />
        {/* FIN-10 §6 per tender: some cheques bounced, the rest still count —
            the grid below marks which. */}
        {receipt?.partiallyReversed ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
            {t('receipts.partiallyReversed')}
          </span>
        ) : null}
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          actions={[
            {
              // Not PART 4.6's Cancel row — a link back to the register wearing
              // the same word. The act the table means is `receipts.cancelDraft`
              // two rows below; the primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/accounting',
            },
            {
              // The document's question and the role's, kept apart (16.7).
              // BR-FIN-03-13 locks every field once the voucher leaves draft,
              // and `locked` is that boundary — so a posted or cancelled
              // voucher has no Delete for anybody, the Owner included
              // (BR-PERM-02, GEN-70), while a reader on a DRAFT one still sees
              // it greyed with the reason.
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && !locked,
              gate: {
                permission: 'accounting.delete',
                reasonKey: 'perm.cannot.deleteReceiptVouchers',
              },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              // PART 4.6's Cancel row proper — the act that ends the voucher's
              // validity, which the primitive's header names by this exact
              // spelling. GEN-59: it asks for a reason and stores it.
              id: 'cancelDraft',
              row: 'cancel',
              label: t('receipts.cancelDraft'),
              variant: 'outline',
              visible: !isNew && !locked,
              gate: {
                permission: 'accounting.edit',
                reasonKey: 'perm.cannot.editReceiptVouchers',
              },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
            {
              // BR-FIN-03-17 — the ONLY action a posted voucher offers. Gone
              // once reversed: one reversal ever (FIN-02 §15).
              //
              // `row: null` IS A STATEMENT HERE, and the tempting alternative
              // is wrong. Reversing does undo a posted voucher's accounting,
              // which is the sentence PART 4.6 writes against **Cancel** — but
              // this screen already declares that row, on `receipts.cancelDraft`
              // above, and the table names buttons by the word printed on them.
              // "Reverse receipt" is not a word in the table, so the table has
              // nothing to say about it and claiming a row would invent a
              // guarantee rather than record one.
              id: 'reverse',
              row: null,
              label: t('receipts.reverseReceipt'),
              icon: <Undo2 aria-hidden />,
              variant: 'destructive',
              visible: status === 'posted' && !receipt?.reversedAt,
              gate: {
                permission: 'accounting.approve',
                reasonKey: 'perm.cannot.reverseReceiptVouchers',
              },
              disabled: busy,
              onAct: () => setReverseAsk(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: !locked,
              gate: {
                permission: isNew ? 'accounting.create' : 'accounting.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createReceiptVouchers'
                  : 'perm.cannot.editReceiptVouchers',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && !locked ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('receipts.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* §8's typed-reason dialog (GEN-59). The title/body split is
              GEN-205's two slots; the body carries the consequence, the
              undone clause and the stored-reason sentence. Inside the
              same conditional as the button, the convention the delete
              above set. `gen72.confirmCancel` is the verb — deliberately
              NOT the trigger's exact words (reason-box.tsx's own rule). */}
          {!isNew && !locked ? (
            <ReasonBox
              open={cancelAsk}
              title={t('receipts.confirmCancelTitle')}
              prompt={t('receipts.confirmCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => void onCancelDraft(reason)}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
          {/* Unconditional, exactly as it already was — unlike the two above
              this box has no conditional to sit inside: `reverseAsk` can only
              be true between the click and the answer, and the click is
              already behind the button's own `visible`. */}
          <ReasonBox
            open={reverseAsk}
            title={t('receipts.reverseReceipt')}
            prompt={t('receipts.reversePrompt')}
            reasonLabel={t('reversal.reasonLabel')}
            confirmLabel={t('reversal.confirm')}
            busy={busy}
            onConfirm={(reason) => void onReverse(reason)}
            onCancel={() => setReverseAsk(false)}
          />
        </DocumentToolbar>
      </div>

      {/* The locked notices (§7) — the reversed notice outranks the posted
          one, FIN-02 §7's priority order read across. Both directions of the
          reversal pair are on screen: this banner links the reversing entry,
          and that entry's own screen links back through reversalOf. */}
      {receipt?.reversedAt ? (
        <div className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          <p>
            {t('receipts.reversedBanner', { date: receipt.reversedAt.slice(0, 10) })}{' '}
            {receipt.reversalJournalEntryId ? (
              <Link
                to={`/accounting/journals/${receipt.reversalJournalEntryId}`}
                className="font-medium underline"
              >
                {t('receipts.reversedByLink', { no: receipt.reversalEntryCode ?? '' })}
              </Link>
            ) : null}
          </p>
          {receipt.reversalReason ? (
            <p className="mt-1 opacity-90">
              {t('receipts.reversedReason', { reason: receipt.reversalReason })}
            </p>
          ) : null}
        </div>
      ) : status === 'posted' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('receipts.lockedPosted')}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD. Rows cancelled
              before the reason existed keep the old sentence. */}
          {receipt?.cancelReason != null
            ? t('receipts.cancelledReasonNotice', { reason: receipt.cancelReason })
            : t('receipts.lockedCancelled')}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── The top information panel (§5) ─────────────────────────────── */}
      <Card>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label htmlFor="rv-date">{t('common.date')}</Label>
              <Input
                id="rv-date"
                type="date"
                dir="ltr"
                value={receiptDate}
                onChange={(event) => setReceiptDate(event.target.value)}
                disabled={!editable}
              />
            </div>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium leading-none">{t('receipts.fType')}</span>
              {/* Flippable on a NEW voucher only — a saved draft keeps its type. */}
              <div className="grid h-12 grid-cols-2 gap-1 rounded-[11px] bg-muted p-1">
                {(['customer', 'other_income'] as const).map((candidate) => (
                  <button
                    key={candidate}
                    type="button"
                    aria-pressed={receiptType === candidate}
                    onClick={() => onPickType(candidate)}
                    disabled={!editable || !isNew}
                    className={cn(
                      'rounded-md text-sm font-medium transition-colors',
                      receiptType === candidate
                        ? 'bg-card shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                      (!editable || !isNew) && receiptType !== candidate && 'opacity-60',
                    )}
                  >
                    {t(
                      candidate === 'customer'
                        ? 'receipts.typeCustomer'
                        : 'receipts.typeOtherIncome',
                    )}
                  </button>
                ))}
              </div>
            </div>
            {receiptType === 'customer' ? (
              <div className="grid gap-1.5">
                <Label htmlFor="rv-customer">{t('receipts.fCustomer')}</Label>
                {/* Each row carries the outstanding balance (§6), and an
                    empty result reads the sentence FIN-03 §5 names. */}
                <PartnerTypeahead
                  entity="customer"
                  id="rv-customer"
                  label={t('receipts.fCustomer')}
                  value={partnerId || null}
                  onChange={(id) => onPickCustomer(id ?? '')}
                  options={customerPicker.options}
                  selected={customerPicker.optionFor(partnerId)}
                  query={customerPicker.query}
                  onQueryChange={customerPicker.setQuery}
                  placeholder={t('sales.pickCustomer')}
                  emptyLabel={t('picker.noMatchingCustomer')}
                  loading={customerPicker.loading}
                  disabled={!editable}
                />
              </div>
            ) : null}
            {/* Per-document currency (2026-10-30): the voucher settles only
                invoices in its OWN currency; a pick clears the allocation. */}
            {receiptType === 'customer' ? (
              <DocCurrencyCell
                idPrefix="rv"
                value={currency}
                onChange={docCurrency.pick}
                disabled={!editable}
              />
            ) : (
              <div className="grid gap-1.5">
                {/* BR-FIN-03-05 — revenue/liability/equity only, never asset or expense. */}
                <Label htmlFor="rv-credit-account">{t('receipts.fCreditAccount')}</Label>
                <NativeSelect
                  id="rv-credit-account"
                  value={creditAccountId}
                  onChange={(event) => setCreditAccountId(event.target.value)}
                  disabled={!editable}
                >
                  <option value="">{t('receipts.pickAccount')}</option>
                  {creditOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.code} — {option.name}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}
          </div>

          {receiptType === 'other_income' ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="grid gap-1.5">
                <Label htmlFor="rv-income-category">{t('receipts.fIncomeCategory')}</Label>
                <Input
                  id="rv-income-category"
                  maxLength={60}
                  value={incomeCategory}
                  onChange={(event) => setIncomeCategory(event.target.value)}
                  disabled={!editable}
                />
              </div>
              <div className="grid gap-1.5 sm:col-span-2 lg:col-span-3">
                <Label htmlFor="rv-income-description">{t('receipts.fIncomeDescription')}</Label>
                <Input
                  id="rv-income-description"
                  maxLength={200}
                  value={incomeDescription}
                  onChange={(event) => setIncomeDescription(event.target.value)}
                  disabled={!editable}
                />
              </div>
            </div>
          ) : null}

        </CardContent>
      </Card>

      {/* ── S6: the tenders — how the money came in (RV-02…RV-06) ───────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenders.title')}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <TendersGrid
            kind="receipt"
            rows={tenders}
            onChange={setTenders}
            editable={editable}
            voucherCurrency={currency}
            baseCurrency={base}
            currencies={currencyCodes}
            accounts={cashOptions}
            rateFor={rateFor}
            today={receiptDate}
          />
        </CardContent>
      </Card>

      {/* ── The allocation grid (§5a) — customer payments only ─────────── */}
      {receiptType === 'customer' && partnerId ? (
        <Card>
          <CardHeader className="flex-row items-center">
            <CardTitle className="me-auto">{t('receipts.allocTitle')}</CardTitle>
            {/* Hidden once locked (§6). */}
            {editable ? (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void onAutoAllocate()}
                  disabled={busy || invoicesLoading}
                >
                  <Wand2 aria-hidden /> {t('receipts.autoAllocate')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onClear} disabled={busy}>
                  <Eraser aria-hidden /> {t('receipts.clear')}
                </Button>
              </div>
            ) : null}
          </CardHeader>
          <CardContent className="grid gap-0">
            {rows.length === 0 && invoiceRange.total === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {/* "No open invoices" is a claim about the customer's BOOKS,
                    so it is now gated on the TOTAL rather than on the rows:
                    a page past the end of a real list holds no rows either,
                    and saying "no open invoices" there would be the paged
                    version of the same lie. That case draws the empty table
                    with the pager under it, which offers the way back.
                    Until the list answers at all, the honest statement is
                    that we do not know yet. */}
                {invoicesLoading ? t('common.loading') : t('receipts.noOpenInvoices')}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('receipts.colInvoiceNo')}</TableHead>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead className="text-end">{t('receipts.colInvoiceTotal')}</TableHead>
                    <TableHead className="text-end">{t('receipts.colAlreadyPaid')}</TableHead>
                    <TableHead className="text-end">{t('receipts.colBalanceDue')}</TableHead>
                    <TableHead className="w-36 text-end">{t('receipts.colApply')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const over = view.overRows.has(row.invoiceId);
                    return (
                      <TableRow key={row.invoiceId} className={cn(over && 'bg-red-50 dark:bg-red-950/40')}>
                        <TableCell>
                          <Link to={`/sales/${row.invoiceId}`} className="font-mono text-primary hover:underline">
                            <bdi dir="ltr">{row.invoiceCode}</bdi>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Ltr value={row.invoiceDate} className="font-mono" />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={row.invoiceTotal} currency={currency} />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={row.alreadyPaid} currency={currency} />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={row.balanceDue} currency={currency} />
                        </TableCell>
                        <TableCell>
                          {/* The one editable cell, tinted to say so (§4). */}
                          <input
                            aria-label={t('receipts.applyTo', { invoice: row.invoiceCode })}
                            dir="ltr"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={alloc[row.invoiceId] ?? ''}
                            onChange={(event) =>
                              setAlloc((current) => ({ ...current, [row.invoiceId]: event.target.value }))
                            }
                            disabled={!editable}
                            className={cn(
                              'h-9 w-full min-w-0 rounded-md border border-input bg-primary/5 px-2 text-end font-mono text-sm tabular-nums outline-none transition-colors',
                              'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
                              'disabled:cursor-not-allowed disabled:opacity-70',
                              over && 'border-destructive text-destructive',
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* NFR-42's visible half (§3 step 12). Drawn only when there is
                more than one page, so a customer with four open invoices is
                not offered a control that can do nothing.

                Turning a page changes what is DRAWN and nothing else: the
                strip above reads `alloc`, Save sends `alloc`, and
                Auto-allocate asks the server. That is the whole reason this
                control could be added at all — see §11. */}
            {invoiceRange.pages > 1 ? (
              <div className="mt-2">
                <Pager
                  page={invoicePaging.page}
                  pages={invoiceRange.pages}
                  first={invoiceRange.first}
                  last={invoiceRange.last}
                  total={invoiceRange.total}
                  onPage={invoicePaging.setPage}
                  // The bare title, which is what every other `<Pager>` in the
                  // product passes. `paging.pagesOf` reads better and is what
                  // the component's own header describes — and NO caller uses
                  // it, so adopting it here would make these two pagers the
                  // odd ones out rather than fixing anything. Reported.
                  label={t('receipts.allocTitle')}
                />
              </div>
            ) : null}

            {/* §5b — the validation strip, colour and message by state. */}
            <div
              role="status"
              className={cn(
                'mt-2 flex flex-wrap items-center gap-2 rounded-b-md border-t-2 px-4 py-3 text-sm',
                strip.className,
              )}
            >
              {strip.icon}
              <span className="font-medium">{strip.text}</span>
              <span className="ms-auto flex gap-6 font-mono text-xs">
                <span>
                  {t('receipts.totalApplied')} <Amount value={view.totalApplied} />
                </span>
                <span>
                  {t('receipts.amountReceived')}{' '}
                  <Amount value={MONEY_RE.test(amount.trim()) ? amount.trim() : '0.00'} />
                </span>
              </span>
            </div>

            {/* BR-FIN-03-09 — the excess is credit, named, never a block. Not
                claimed before the grid is real: see stripState above. */}
            {stripState === 'credit' ? (
              <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('receipts.creditBanner')} <Amount value={view.unallocated} currency={currency} />
              </p>
            ) : null}

            {/* Wave 4A — the customer's UNAPPLIED credit notes as a second
                source beside the cash: each can be set against the open
                invoices on this page. Applying one moves no money (the
                credit already sits on the account), so it rides no voucher
                state — it is the note's own act, and refreshes the grid. */}
            <CreditNoteSources
              partnerId={partnerId}
              currency={currency}
              invoices={rows.map((row) => ({
                invoiceId: row.invoiceId,
                invoiceCode: row.invoiceCode,
                balanceDue: row.balanceDue,
              }))}
            />
          </CardContent>
        </Card>
      ) : null}

      {/* ── The posting panel + notes ──────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('receipts.notes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              aria-label={t('receipts.notes')}
              placeholder={t('receipts.notesPh')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid gap-3">
            {/* §12's preview-in-words, kept as the spec asks. */}
            <p className="text-xs text-muted-foreground">
              {receiptType === 'customer'
                ? t('receipts.postingPreview', { account: accountCode ?? '…' })
                : t('receipts.postingPreviewOther', {
                    account: accountCode ?? '…',
                    credit: creditOption ? `${creditOption.code} — ${creditOption.name}` : '…',
                  })}
            </p>
            {/* A settlement freezes its own rate too, and it has to: the
                receipt and the invoice it pays are two documents at two rates,
                and the gap between them is GEN-113's realised exchange gain or
                loss. What is converted here is the AMOUNT RECEIVED, not a
                grand total. Hidden entirely for a base-currency voucher. */}
            <FxPanel doc={receipt} />
            {!locked ? (
              <Gated
                permission="accounting.approve"
                reasonKey="perm.cannot.postReceiptVouchers"
                className="w-full"
              >
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  disabled={busy || !postable}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('receipts.postReceipt')}
                </Button>
              </Gated>
            ) : null}
            {/* SPEC-VERSUS-BUILD DEVIATION, and deliberately carrying NO rule
                identifier. FIN-03 has no approval rule and no approval row:
                its rules run to BR-FIN-03-17 and its error table to
                ERR-FIN-03-08 with nothing about approving, and a search of the
                whole screen section for "approv" returns nothing. This banner
                applies PART 16's product-wide approval pattern to a screen the
                spec never gave one — a reasonable build decision, but not a
                rule being enforced, so there is nothing true to cite here.

                The SENTENCE is spec-derived even though the banner is not:
                "sent to an approver" is the document's phrase in all six
                places this refusal appears. It read "posting is for an
                approver" until the ERR sweep — a string with no basis anywhere
                in the specification. Pinned across all five banners in
                spec-wording.spec.ts. */}
            {!locked && !canPost ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('receipts.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
